import { DesignSettings } from '../types/DesignSettings';
import { CSSProperties } from 'react';

export const DEFAULT_DESIGN_SETTINGS: DesignSettings = {
  backgroundColor: '#FFFFFF',
  textColor: '#333333',
  fontFamily: 'Arial',
  fontSize: 13,
  nameFontSize: 16,
  lineHeight: 1.08,
  padding: 10,
  borderRadius: 0,
  borderColor: '#000000',
  borderWidth: 0,
  shadowColor: '#000000',
  shadowBlur: 0,
  shadowOffset: 0,
  contactColor: '#212121',
  fontScale: 0,
  lineSpacing: 100,
  socialMediaIconType: 'filled',
  socialMediaIconShape: 'square',
  socialMediaIconSize: 24,
  themeColor: '#45668E',
  headerBarColor: '#FFFFFF',
  imageVerticalAlign: 'top',
  imageHorizontalAlign: 'left',
  customSpace: 0,
  spaceBelowFarewell: 4,
  separatorType: 'line',
  separatorColor: '#646464',
  direction: 'ltr',
  lineStyle: 'solid',
  horizontalLineStyle: 'none',
  headerColor: '#646464',
  subHeaderColor: '#646464',
  bodyColor: '#212121',
  imageShape: 'square',
  labelType: 'none',
  imageSize: 80,
  layoutType: 'horizontal',
  socialIconSpacing: 6,
  useThemeColorForContactInfo: false,
  decorativeLineColor: '#646464',
  verticalAlignment: 'center',
  displayProfileImage: true, // Explicit default - can be toggled off by user (Starter+ tier)
  socialBanner: {
    backgroundColor: '#45668E',
    iconAlignment: 'right',
  },
  farewellSignature: {
    signOffType: 'signoff',
    signOffSize: 32,
    signOffAlignment: 'left',
    signOffSignOff: 'sincerely',
    signOffFont: 'boss_signature'
  },
  analytics: {
    enabled: false,
    // Analytics Name ID
    analyticsNameId: 'Name ID',
    // UTM Parameters
    utmSource: 'Company Name',
    utmMedium: 'email',
    utmCampaign: 'signature',
    // Basic Tracking
    trackClicks: true,
    trackConversions: true,
    trackBounceRate: false,
    trackGeoDevice: true,
    // Pixel Tracking
    enablePixelTracking: false,
    pixelPlacement: 'footer',
    pixelSize: '1x1',
    // Advanced Tracking
    trackSessionDuration: false,
    trackPageViews: false,
    trackRevenue: false
  }
};

// Common styles used across all templates
export const DEFAULT_STYLES = {
  body: {
    fontFamily: 'Arial',
    fontSize: 14,
    lineHeight: 1.2,
    color: '#212121',
    padding: '0.01px 0.01px 0.01px 0.01px',
    verticalAlign: 'top',
    margin: 0,
    border: 0,
    outline: 0,
    textDecoration: 'none'
  } as CSSProperties,
  name: {
    fontWeight: 'bold',
    letterSpacing: '0px',
    whiteSpace: 'nowrap'
  } as CSSProperties,
  title: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap'
  } as CSSProperties,
  contact: {
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  } as CSSProperties,
  contactInfo: {
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    margin: '0.1px',
    padding: '0.01px 0.01px 0.01px 0.01px'
  } as CSSProperties
};

// Template-specific styles
export const TEMPLATE_STYLES = {
  strip: {
    container: {
      width: '100%',
      maxWidth: 600,
      margin: '0 auto'
    } as CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    } as CSSProperties,
    name: {
      fontWeight: 'bold',
      letterSpacing: '0px',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    title: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    socialBanner: {
      td: {
        height: '49px',
        // Padding removed - now handled dynamically in SocialBanner component based on background color
        lineHeight: '0%'
      } as CSSProperties,
      table: {
        float: 'right' as const,
        textAlign: 'right' as const
      } as CSSProperties,
      iconTd: {
        float: 'right' as const
      } as CSSProperties
    }
  },
  classic: {
    container: {
      width: '100%',
      maxWidth: 600,
      margin: '0 auto'
    } as CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    } as CSSProperties,
    name: {
      fontWeight: 'bold',
      letterSpacing: '0px',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    title: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    socialBanner: {
      td: {
        // Padding removed - now handled dynamically in SocialBanner component based on background color
        lineHeight: 0,
        margin: 0,
        width: '100%',
        display: 'block'
      } as CSSProperties,
      table: {
        width: '100%',
        margin: 0,
        padding: 0,
        borderCollapse: 'collapse'
      } as CSSProperties,
      iconTd: {
        margin: 0,
        padding: 0,
        float: 'left' as const
      } as CSSProperties
    }
  },
  horizontal: {
    container: {
      width: '100%',
      maxWidth: 600,
      margin: '0 auto'
    } as CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    } as CSSProperties,
    name: {
      fontWeight: 'bold',
      letterSpacing: '0px',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    title: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    socialBanner: {
      td: {
        height: 0,
        // Padding removed - now handled dynamically in SocialBanner component based on background color
        lineHeight: '0%'
      } as CSSProperties,
      table: {
        float: 'right' as const,
        textAlign: 'right' as const
      } as CSSProperties,
      iconTd: {
        float: 'right' as const
      } as CSSProperties
    },
    imageContainer: {
      padding: '0.01px 0.01px 14px 1px',
      width: 70,
      textAlign: 'left'
    } as CSSProperties,
    contentContainer: {
      padding: '0.01px 0.01px 0.01px 0.01px',
      verticalAlign: 'top'
    } as CSSProperties
  },
  wide: {
    container: {
      width: '100%',
      maxWidth: 600,
      margin: '0 auto'
    } as CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    } as CSSProperties,
    name: {
      fontWeight: 'bold',
      letterSpacing: '0px',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    title: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    socialBanner: {
      td: {
        height: 0,
        // Padding removed - now handled dynamically in SocialBanner component based on background color
        lineHeight: '0%'
      } as CSSProperties,
      table: {
        float: 'right' as const,
        textAlign: 'right' as const
      } as CSSProperties,
      iconTd: {
        float: 'right' as const
      } as CSSProperties
    },
    imageContainer: {
      padding: '0.01px 0.01px 14px 1px',
      width: 65,
      textAlign: 'left'
    } as CSSProperties,
    contentContainer: {
      padding: '0.01px 0.01px 0.01px 14px',
      verticalAlign: 'top'
    } as CSSProperties
  },
  stacked: {
    container: {
      width: '100%',
      maxWidth: 600,
      margin: '0 auto'
    } as CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    } as CSSProperties,
    name: {
      fontWeight: 'bold',
      letterSpacing: '0px',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    title: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    socialBanner: {
      td: {
        height: 0,
        // Padding removed - now handled dynamically in SocialBanner component based on background color
        lineHeight: '0%'
      } as CSSProperties,
      table: {
        float: 'right' as const,
        textAlign: 'right' as const
      } as CSSProperties,
      iconTd: {
        float: 'right' as const
      } as CSSProperties
    },
    imageContainer: {
      padding: '0.01px 0.01px 14px 1px',
      width: 65,
      textAlign: 'left'
    } as CSSProperties,
    contentContainer: {
      padding: '0.01px 0.01px 0.01px 14px',
      verticalAlign: 'top'
    } as CSSProperties
  },
  compact: {
    container: {
      width: '100%',
      maxWidth: 600,
      margin: '0 auto'
    } as CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    } as CSSProperties,
    imageContainer: {
      padding: '0.01px 14px 0.01px 1px',
      width: '82.5px',
      minWidth: '82.5px',
      textAlign: 'center',
      height: '100%',
      verticalAlign: 'top'
    } as CSSProperties,
    contentContainer: {
      verticalAlign: 'top',
      padding: '0.01px 0.01px 0.01px 0.01px'
    } as CSSProperties,
    name: {
      fontWeight: 'bold',
      letterSpacing: '0px',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    title: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap'
    } as CSSProperties,
    socialBanner: {
      td: {
        // Padding removed - now handled dynamically in SocialBanner component based on background color
        lineHeight: 0,
        margin: 0,
        width: '100%',
        display: 'block'
      } as CSSProperties,
      table: {
        width: '100%',
        margin: 0,
        padding: 0,
        borderCollapse: 'collapse'
      } as CSSProperties,
      iconTd: {
        margin: 0,
        padding: 0,
        float: 'left' as const
      } as CSSProperties
    }
  }
};

export const STRIP_TEMPLATE_DEFAULTS: DesignSettings = {
  ...DEFAULT_DESIGN_SETTINGS
};

export const defaultPersonalInfo = {
  name: 'John Doe',
  title: 'Software Engineer',
  department: 'Engineering',
  email: 'john.doe@example.com',
  phone: '+1 (555) 123-4567',
  mobile: '+1 (555) 987-6543',
  company: 'Example Corp',
  position: 'Senior Engineer',
  office: 'New York',
  fax: '+1 (555) 123-4568',
  skype: 'john.doe',
  website: 'www.example.com',
  address: '123 Main St, New York, NY 10001',
  bio: 'Passionate about creating innovative solutions'
}; 